import type { FontAwesomeWeight, IconName } from '@/shared/ui/icon'

export interface ReportTabIcon {
  name: IconName
  weight: FontAwesomeWeight
  size?: number
}

function icon(name: IconName, weight: FontAwesomeWeight, size?: number): ReportTabIcon {
  return size === undefined ? { name, weight } : { name, weight, size }
}

const light = (name: IconName, size?: number): ReportTabIcon => icon(name, 'light', size)
const regular = (name: IconName, size?: number): ReportTabIcon => icon(name, 'regular', size)
const solid = (name: IconName, size?: number): ReportTabIcon => icon(name, 'solid', size)
const kit = (name: IconName, size?: number): ReportTabIcon => icon(name, 'kit', size)

/** Exact v3 report-tab glyph catalog, resolved through the shared Font Awesome component. */
const REPORT_TAB_ICONS: Readonly<Record<string, ReportTabIcon>> = {
  overview: light('layer-group'),
  overall: light('layer-group'),
  image: light('images'),
  video: kit('video-monitor', 18),
  text: light('text'),
  'creative benchmark': light('magnifying-glass-chart'),

  products: light('box'),
  product: light('box'),
  'product customization': light('box'),
  catalog: light('boxes-packing'),
  category: light('boxes-stacked'),

  campaign: light('folder-arrow-up'),
  basics: light('folder-arrow-up'),
  'ad group': light('folder-tree'),
  ad: light('list-tree'),
  'key events': light('hexagon-nodes'),
  'custom conversions and events': light('hexagon-nodes'),
  asset: light('hexagon-nodes'),
  extensions: light('gallery-thumbnails'),
  'keyword trends': light('arrow-trend-up'),
  'ad library': regular('image-stack'),

  creatives: light('photo-film'),
  placements: light('list-dropdown'),
  'custom definitions': light('list-dropdown'),
  'asset groups': light('object-group'),
  'search term': kit('list-magnifying-glass', 20),
  'search terms': kit('list-magnifying-glass', 20),

  audience: light('users'),
  targeting: solid('users-viewfinder'),
  'source/medium': light('line-columns'),
  keywords: light('line-columns'),
  'organic keywords': light('globe'),
  'paid & organic': light('hand-holding-dollar'),
  'age gender': solid('person-half-dress', 18),
  location: regular('location-dot', 18),
  demographics: light('globe'),
  device: light('mobile-screen-button'),
  behavior: light('chart-line'),
  interest: light('heart'),
  'landing page': light('window-restore'),
  'bundle sales': light('cart-flatbed-boxes'),
  recommendations: solid('comment-plus'),
  anomalies: solid('bug'),
  'social platform': light('thumbs-up'),
  'display channels': light('display'),

  profile: solid('user'),
  users: solid('users'),
  accounts: solid('id-badge'),
  account: regular('clipboard-list'),
  workspace: solid('globe'),
  connections: solid('network-wired'),
  notifications: solid('bell'),
  permissions: solid('address-book'),
  subscription: solid('bookmark'),

  'all creatives': solid('image-stack'),
  'what if': solid('circle-question'),
  'ad texts': solid('text'),
  'production brief': solid('file-invoice'),
}

export function reportTabIcon(name: string): ReportTabIcon | null {
  const key = name.trim().toLowerCase()
  return REPORT_TAB_ICONS[key] ?? REPORT_TAB_ICONS[key.replaceAll(/[-_]/gu, ' ')] ?? null
}
